import { FileCategory, FileUploadState } from "./types";

import { CATEGORY_ICON_MAP, EXTENSION_CATEGORY_MAP } from "./constants";

import { LucideIcon } from "lucide-react";

import React from "react";

const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
        return "";
    }
    return filename.slice(lastDotIndex + 1).toLowerCase();
};

const getFileCategory = (filename: string): FileCategory => {
    const extension = getFileExtension(filename);
    return EXTENSION_CATEGORY_MAP[extension] ?? "unknown";
};

/**
 * Gets the appropriate Lucide icon component for a file
 * @param filename - The name of the file
 * @returns The Lucide icon component for the file's category
 */
export const getFileIcon = (filename: string): LucideIcon => {
    const category = getFileCategory(filename);
    return CATEGORY_ICON_MAP[category];
};

export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

export const getCurrentState = (disabled: boolean, dragActive: boolean): FileUploadState => {
    if (disabled) {
        return "disabled";
    }

    if (dragActive) {
        return "dragActive";
    }
    return "default";
};

export const createClickHandler = <T extends unknown[]>(callback: (...args: T) => void, ...args: T) => {
    return (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        callback(...args);
    };
};

export const getFileNameWithoutExtension = (fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf(".");
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
};

/**
 * Checks if a file matches the accepted file types
 * @param file - The file to validate
 * @param accept - Array of accepted file types (MIME types or extensions)
 * @returns True if file is accepted, false otherwise
 */
export const isFileTypeAccepted = (file: File, accept: string[]): boolean => {
    // If no accept list is provided, accept all files
    if (!accept || accept.length === 0) {
        return true;
    }

    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    return accept.some((acceptedType) => {
        const type = acceptedType.toLowerCase().trim();

        // Handle wildcard MIME types (e.g., "image/*")
        if (type.includes("*")) {
            const [mainType] = type.split("/");
            return fileType.startsWith(`${mainType}/`);
        }

        // Handle MIME type match
        if (type.includes("/")) {
            return fileType === type;
        }

        // Handle extension match (e.g., ".pdf", ".jpg")
        if (type.startsWith(".")) {
            return fileName.endsWith(type);
        }

        // Handle extension without dot (e.g., "pdf", "jpg")
        return fileName.endsWith(`.${type}`);
    });
};

/**
 * Filters files based on the accepted file types
 * @param files - Array of files to filter
 * @param accept - Array of accepted file types
 * @returns Array of files that match the accept criteria
 */
export const filterAcceptedFiles = (files: File[], accept: string[]): File[] => {
    return files.filter((file) => isFileTypeAccepted(file, accept));
};
